import React from "react";
import { View, Text, FlatList, Pressable, StyleSheet } from "react-native";
import CustomText from "../../common/general/CustomText/CustomText";
import { CircleAlert, Clock, Eye } from "lucide-react";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import { RootStackParamList } from "../../../navigation/navigation.types";
import { Drawer } from "../Drawer/Drawer";

interface StudentListDrawerProps {
  activeCourse: any[];
  courseWiseRosterInsights: any[];
  getInitials: (name: string) => string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  totalAtRiskCount: number;
}
const lastInterventionStatusColor = {
  pending: "#b54708",
  "no-response": "#667085",
  completed: "#3b7c0f",
  "needs-follow-up": "#3b7c0f",
};
export default function StudentListDrawer({
  activeCourse,
  courseWiseRosterInsights,
  getInitials,
  isOpen,
  onOpenChange,
  totalAtRiskCount,
}: StudentListDrawerProps) {
  function formatStatusLabel(status: string): string {
    return status
      .split(/[-_\s]/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  }

  const coursesWithStudents = activeCourse.filter((course) => {
    const students = courseWiseRosterInsights?.find((i) => i?.data?.courseId === course.id)?.data?.students;
    return students && students.length > 0;
  });

  const navigation = useNavigation<NavigationProp<RootStackParamList>>();

  const renderStudent = ({ item, courseCode }: { item: any; courseCode: string }) => (
    <View style={styles.card}>
      <View style={styles.infoRow}>
        <View style={styles.avatar}>
          <CustomText
            text={getInitials(item?.studentName)}
            weight="bold"
            style={styles.avatarText}
            useTranslationText={false}
          />
        </View>

        <View style={{ flex: 1 }}>
          <View style={styles.headerRow}>
            <View>
              <CustomText
                text={item?.studentName}
                size="m"
                useTranslationText={false}
                textType="text"
                weight="bold"
                role="heading"
                aria-level="1"
                style={{ color: "#101828" }}
              />
              <CustomText
                text={courseCode ? courseCode : ""}
                size="s"
                useTranslationText={false}
                textType="text"
                weight="normal"
                role="heading"
                aria-level="1"
                style={{ color: "#344054" }}
              />
            </View>
            <View>
              <CustomText
                text={formatStatusLabel(item.risk || item.riskLevel)}
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="normal"
                role="heading"
                aria-level="1"
                style={styles.badgeText}
              />
            </View>
          </View>
          <View style={styles.metrics}>
            <CustomText
              text={item.progress || item.progressPct + "% progress"}
              size="s"
              useTranslationText={false}
              textType="text"
              weight="normal"
              role="heading"
              aria-level="1"
              style={{ color: "#344054" }}
            />
            {item.statusSummary.includes("Last") && (
              <CustomText
                text={"•"}
                size="s"
                useTranslationText={false}
                textType="text"
                weight="normal"
                role="heading"
                aria-level="1"
                style={{ color: "#344054" }}
              />
            )}

            {item.statusSummary.includes("Last") && (
              <CustomText
                text={item.statusSummary}
                size="s"
                useTranslationText={false}
                textType="text"
                weight="normal"
                role="heading"
                aria-level="1"
                style={{ color: "#344054" }}
              />
            )}
          </View>

          {/* Intervention */}
          {item.lastIntervention ? (
            <View style={styles.lastInterventionContainer}>
              <Clock style={styles.clockIcon} />
              <View style={styles.lastInterventionText}>
                <CustomText
                  text={"Message sent " + item.lastIntervention.daysAgo + "d ago"}
                  size="xs"
                  useTranslationText={false}
                  textType="text"
                  weight="normal"
                  role="heading"
                  aria-level="1"
                  style={{ color: "#344054" }}
                />
                <CustomText
                  text={" • "}
                  size="xs"
                  useTranslationText={false}
                  textType="text"
                  weight="normal"
                  role="heading"
                  aria-level="1"
                  style={{ color: "#344054" }}
                />
                <CustomText
                  text={item.lastIntervention.status}
                  size="xs"
                  useTranslationText={false}
                  textType="text"
                  weight="normal"
                  role="heading"
                  aria-level="1"
                  style={{ color: lastInterventionStatusColor[item.lastIntervention.status] }}
                />
              </View>
            </View>
          ) : (
            item.statusSummary.toLowerCase().includes("no contact") && (
              <View style={styles.noContactContainer}>
                <CircleAlert style={styles.noContactTag} />
                <CustomText
                  text={"No contact yet"}
                  size="xs"
                  useTranslationText={false}
                  textType="text"
                  weight="normal"
                  role="heading"
                  aria-level="1"
                  style={{ color: "#b91c1c" }}
                />
              </View>
            )
          )}
        </View>
      </View>

      {/* Actions */}
      <View style={styles.actions}>
        <Pressable
          style={styles.secondaryButton}
          onPress={() => {
            onOpenChange(false);
            navigation.navigate("Student Profile", {
              studentId: item.userId,
              returnTo: "Admin Dashboard",
            });
          }}>
          <View style={styles.secondaryButtonText}>
            <Eye style={styles.eyeIcon} />
            <CustomText
              text="View Profile"
              size="s"
              useTranslationText={false}
              textType="text"
              weight="normal"
              role="heading"
              aria-level="1"
              style={{ color: "#344054" }}
            />
          </View>
        </Pressable>
      </View>
    </View>
  );

  const content =
    coursesWithStudents.length === 0 ? (
      <View style={styles.emptyState}>
        <Text style={styles.emptyTitle}>No students found</Text>
        <Text style={styles.emptyText}>All students are doing well!</Text>
      </View>
    ) : (
      <View style={{ gap: 12 }}>
        {coursesWithStudents.map((course) => {
          const students = courseWiseRosterInsights?.find((i) => i?.data?.courseId === course.id)?.data?.students || [];

          return (
            <FlatList
              key={course.id}
              data={students}
              keyExtractor={(item) => `${item.userId}+${course.course_code}`}
              renderItem={({ item }) => renderStudent({ item, courseCode: course.course_code })}
              contentContainerStyle={styles.list}
              scrollEnabled={false}
            />
          );
        })}
      </View>
    );
  return (
    <Drawer
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="At-Risk Students"
      description={`${totalAtRiskCount} students`}>
      {content}
    </Drawer>
  );
}

const styles = StyleSheet.create({
  list: {
    gap: 12,
  },
  noContactTag: { height: 14, width: 14, color: "#b91c1c" },
  card: {
    backgroundColor: "white",
    borderRadius: 32,
    padding: 20,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },

  infoRow: {
    flexDirection: "row",
    gap: 16,
    marginBottom: 12,
  },

  avatar: {
    width: 48,
    height: 48,
    borderRadius: "50%",
    backgroundColor: "#CCD7ff",
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: "#3459D4",
    fontSize: 16,
  },

  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 8,
  },

  badgeText: {
    fontSize: 12,
    color: "#b91c1c",
    backgroundColor: "#b91c1c0D",
    borderWidth: 1,
    borderColor: "#b91c1c",
    paddingHorizontal: 10,
    paddingVertical: 2,
    borderRadius: 16,
  },

  metrics: {
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
    marginBottom: 12,
  },

  metricDot: {
    fontSize: 13,
  },
  noContactContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    backgroundColor: "#b91c1c0D",
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#b91c1c1A",
    alignSelf: "flex-start",
  },

  actions: {
    flexDirection: "row",
    gap: 8,
    borderTopWidth: 1,
    borderTopColor: "#e5e7eb",
    paddingTop: 12,
  },

  primaryButton: {
    flex: 1,
    backgroundColor: "#2563eb",
    paddingVertical: 10,
    borderRadius: 8,
    alignItems: "center",
  },

  primaryButtonText: {
    color: "white",
    fontWeight: "600",
    fontSize: 13,
  },

  secondaryButton: {
    flex: 1,
    backgroundColor: "#f3f4f6",
    paddingVertical: 10,
    borderRadius: 16,
    alignItems: "center",
  },

  secondaryButtonText: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontSize: 13,
    fontWeight: "500",
  },

  emptyState: {
    alignItems: "center",
    paddingVertical: 40,
  },

  emptyTitle: {
    fontWeight: "600",
    fontSize: 16,
  },

  emptyText: {
    fontSize: 14,
    color: "#6b7280",
  },
  eyeIcon: { height: 16, width: 16 },
  clockIcon: { height: 14, width: 14, color: "#075985" },
  lastInterventionContainer: { display: "flex", flexDirection: "row", alignItems: "center", gap: 8 },
  lastInterventionText: { display: "flex", flexDirection: "row", alignItems: "center" },
});
